/**
 * Named text styles: a colour and a typeface, saved once in the document's
 * own formatting and then applied to a stretch of prose by name.
 *
 * Deliberately not free CSS. `colour.ts` already explains why a colour
 * reaching a stylesheet has to be validated rather than trusted: a permissive
 * check would let a report rewrite the page of whoever it is shared with.
 * Free-form CSS text would be far worse than a loose colour, since `content`
 * paired with a counter, or a `background: url(...)`, can read the page back
 * to whoever wrote it — and this application's PDF and HTML export both run
 * the stylesheet server-side too, which turns that into a request the server
 * makes on the author's behalf. So a style here is exactly two properties,
 * both already the kind of value this application trusts in a stylesheet: a
 * `#rrggbb` colour, the same rule the accent uses, and a typeface from the
 * fixed list the whole document is already restricted to.
 *
 * A style rebuilt from an imported PDF carries more, because it has to set
 * text exactly as the PDF did: the font the PDF used (one of the document's
 * imported fonts, by key), a size, a line height, a weight and a slant. Each
 * is a number or a name checked on the way in, never text that reaches the
 * stylesheet unread, so the reasoning above holds for them as well.
 *
 * Pure. Reading, validating, and turning them into CSS text; nothing here
 * touches the DOM. Something has to put that CSS text where the browser can
 * read it — see `features/typography/textStyleSheet.ts`.
 */
import { fontStack, type ImportedFont } from '@/domain/facsimile/fonts'
import { isAccent } from '@/domain/layout/colour'
import { isTypefaceId, typefaceOf, type TypefaceId } from '@/domain/typeface'

/** A name, a colour and a typeface, ready to apply to a selection. */
export interface TextStyle {
  readonly id: string
  readonly name: string
  /** `#rrggbb`. See `isAccent`: the same rule the document accent follows. */
  readonly color: string
  readonly typeface: TypefaceId
  /**
   * One of the document's imported fonts, by key, drawn through its stack
   * (see `domain/facsimile/fonts.ts`) in place of the typeface. The typeface
   * stands in if the document no longer holds that font.
   */
  readonly font?: string
  /** In points. */
  readonly size?: number
  /** Baseline to baseline, as a multiple of the size. */
  readonly lineHeight?: number
  /** Space after every letter, in ems: the tracking a title was set with. */
  readonly letterSpacing?: number
  /** Space after a paragraph, and between two items of a list, in ems. */
  readonly paragraphSpacing?: number
  readonly itemSpacing?: number
  readonly weight?: number
  readonly italic?: boolean
}

/** What a font key may be: the characters an imported font's file name allows. */
const FONT_KEY = /^[A-Za-z0-9_.,+-]{1,120}$/

const SIZE_PT = { min: 4, max: 200 }
const LINE_HEIGHT = { min: 0.5, max: 3 }
const LETTER_SPACING_EM = { min: -0.3, max: 1 }
const SPACING_EM = { min: 0, max: 4 }
const WEIGHT = { min: 100, max: 900 }

/**
 * The class a span carrying a text style is given, by its id.
 *
 * Prefixed rather than the bare id: an id is usually a UUID, and a CSS class
 * cannot start with a digit. The prefix also keeps this recognisable as ours
 * next to whatever else ends up in a `class` attribute.
 */
const CLASS_PREFIX = 'text-style--'

export function textStyleClassName(id: string): string {
  return `${CLASS_PREFIX}${id}`
}

/** The style id a class name carries, or nothing if it does not name one. */
export function textStyleIdFromClass(className: string): string | null {
  return className.startsWith(CLASS_PREFIX) ? className.slice(CLASS_PREFIX.length) : null
}

/**
 * Styles read from a stored document.
 *
 * An entry that fails to validate is dropped rather than repaired — the same
 * choice `readAccent` makes, for the same reason: making up a colour or a
 * font nobody chose is a worse surprise than losing the one style that was
 * already broken, and it keeps every id here safe to use as a CSS class and
 * every colour safe to write into a stylesheet.
 */
export function readTextStyles(source: unknown): readonly TextStyle[] {
  if (!Array.isArray(source)) return []

  const styles: TextStyle[] = []
  for (const entry of source) {
    if (typeof entry !== 'object' || entry === null) continue
    const record = entry as Record<string, unknown>

    const id = record['id']
    const name = record['name']
    const color = record['color']
    const typeface = record['typeface']

    if (typeof id !== 'string' || id === '') continue
    if (typeof name !== 'string' || name.trim() === '') continue
    if (typeof color !== 'string' || !isAccent(color)) continue
    if (typeof typeface !== 'string' || !isTypefaceId(typeface)) continue

    styles.push({
      id,
      name: name.trim(),
      color: color.toLowerCase(),
      typeface,
      ...importedSettings(record),
    })
  }
  return styles
}

function within(value: unknown, range: { min: number; max: number }): value is number {
  return (
    typeof value === 'number' && Number.isFinite(value) && value >= range.min && value <= range.max
  )
}

/**
 * The settings a style imported from a PDF adds, each kept only when it is
 * valid and simply left out otherwise: a style that loses its size still sets
 * its colour and font, which is closer to what was meant than losing it whole.
 */
function importedSettings(
  record: Record<string, unknown>,
): Pick<
  TextStyle,
  | 'font'
  | 'size'
  | 'lineHeight'
  | 'letterSpacing'
  | 'paragraphSpacing'
  | 'itemSpacing'
  | 'weight'
  | 'italic'
> {
  const { font, size, lineHeight, letterSpacing, paragraphSpacing, itemSpacing, weight, italic } =
    record
  return {
    ...(typeof font === 'string' && FONT_KEY.test(font) ? { font } : {}),
    ...(within(size, SIZE_PT) ? { size } : {}),
    ...(within(lineHeight, LINE_HEIGHT) ? { lineHeight } : {}),
    ...(within(letterSpacing, LETTER_SPACING_EM) ? { letterSpacing } : {}),
    ...(within(paragraphSpacing, SPACING_EM) ? { paragraphSpacing } : {}),
    ...(within(itemSpacing, SPACING_EM) ? { itemSpacing } : {}),
    ...(within(weight, WEIGHT) ? { weight: Math.round(weight / 100) * 100 } : {}),
    ...(typeof italic === 'boolean' ? { italic } : {}),
  }
}

/** The font-family a style is set in: its imported font's stack when the document holds it, else its typeface. */
export function styleStack(style: TextStyle, fonts: readonly ImportedFont[] = []): string {
  const imported = style.font ? fonts.find((font) => font.key === style.font) : undefined
  if (imported) return fontStack(imported)
  return typefaceOf(style.typeface).stack
}

/**
 * How the paragraphs and list items of a text set in a style are spaced, as printed.
 *
 * The page spaces paragraphs and lists its own way; a style imported from a
 * PDF says how its own were spaced, beyond the line pitch, and holds whatever
 * text it is applied to to that. An item's paragraph adds nothing of its own.
 */
function spacingRules(style: TextStyle, name: string): string[] {
  const rules: string[] = []
  if (within(style.paragraphSpacing, SPACING_EM)) {
    const space = `${style.paragraphSpacing}em`
    rules.push(
      `.page .${name} p, .page .${name} ul, .page .${name} ol { margin-bottom: ${space}; }`,
    )
  }
  if (within(style.itemSpacing, SPACING_EM)) {
    rules.push(
      `.page .${name} li { margin-bottom: ${style.itemSpacing}em; }`,
      `.page .${name} li > p { margin-bottom: 0; }`,
    )
  }
  return rules
}

/**
 * The stylesheet every named style becomes: one rule per style, keyed by the
 * class `textStyleClassName` writes.
 *
 * A style removed from the catalogue simply stops having a rule here — the
 * prose that still names it keeps the class but loses the styling, rather
 * than breaking or falling back to something nobody chose.
 *
 * A style naming one of the document's imported fonts is drawn in it.
 */
export function textStyleSheet(
  styles: readonly TextStyle[],
  fonts: readonly ImportedFont[] = [],
): string {
  return styles
    .map((style) => {
      // Re-checked rather than trusted, even though a validated `TextStyle`
      // should already be safe: this text reaches a real stylesheet, and
      // that is the one place an unchecked value must never reach.
      if (!isAccent(style.color)) return ''

      const declarations = [`color: ${style.color}`, `font-family: ${styleStack(style, fonts)}`]
      if (within(style.size, SIZE_PT)) declarations.push(`font-size: ${style.size}pt`)
      if (within(style.lineHeight, LINE_HEIGHT))
        declarations.push(`line-height: ${style.lineHeight}`)
      if (within(style.letterSpacing, LETTER_SPACING_EM) && style.letterSpacing !== 0) {
        declarations.push(`letter-spacing: ${style.letterSpacing}em`)
      }
      if (within(style.weight, WEIGHT))
        declarations.push(`font-weight: ${Math.round(style.weight)}`)
      if (style.italic === true) declarations.push('font-style: italic')

      // Also under `.page`, so a heading wearing a style is set in it rather
      // than in the page's own heading rule, which is as specific as a class.
      const name = textStyleClassName(style.id)
      return [
        `.${name}, .page .${name} { ${declarations.join('; ')}; }`,
        ...spacingRules(style, name),
      ].join('\n')
    })
    .filter((rule) => rule !== '')
    .join('\n')
}
